import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { NanaeHeader, NanaeFooter } from "@/components/qv3";
import { fetchExpert, categoryLabel } from "@/lib/experts";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/experts/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Catalogue Hōjin` },
      { name: "description", content: `Fiche détaillée de l'expert ${params.slug} dans le catalogue Hōjin : compétences, contexte, livrables.` },
      { name: "robots", content: "noindex" },
      ...socialMeta({
        title: `${params.slug} — Catalogue Hōjin`,
        description: `Fiche détaillée de l'expert ${params.slug} dans le catalogue Hōjin : compétences, contexte, livrables.`,
        url: `https://hojin.ai/experts/${params.slug}`,
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/experts/${params.slug}` }],
  }),
  component: ExpertDetailPage,
});

function ExpertDetailPage() {
  return (
    <ProtectedRoute>
      <div className="nanae-page qv3-dark">
        <NanaeHeader variant="app" title="Expert" />
        <ExpertDetailContent />
        <NanaeFooter variant="minimal" />
      </div>
    </ProtectedRoute>
  );
}

function ExpertDetailContent() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { data: expert, isLoading, isError, error } = useQuery({
    queryKey: ["expert", slug],
    queryFn: () => fetchExpert(slug),
  });

  return (
    <main
      style={{
        background: "var(--paper)",
        minHeight: "100vh",
        padding: "48px 24px 80px",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <Link
          to="/experts/catalogue"
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: 2,
            color: "var(--smoke)",
            textDecoration: "none",
            textTransform: "uppercase",
          }}
        >
          ← Catalogue
        </Link>

        {isLoading ? (
          <p
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              color: "var(--smoke)",
              marginTop: 48,
            }}
          >
            Chargement…
          </p>
        ) : isError || !expert ? (
          <div role="alert" style={{ marginTop: 24, color: "var(--leather)" }}>
            Erreur — {error instanceof Error ? error.message : "Expert introuvable"}
          </div>
        ) : (
          <>
            <header style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 24 }}>
              <div
                aria-hidden
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 8,
                  background: "var(--gold-tint)",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 900,
                  color: "var(--leather)",
                  fontSize: 32,
                }}
              >
                {expert.icon ?? expert.name.charAt(0)}
              </div>
              <div>
                <h1
                  style={{
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 700,
                    fontSize: 36,
                    margin: "0 0 4px",
                    color: "var(--charcoal-deep)",
                  }}
                >
                  {expert.name}
                </h1>
                <span
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 11,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "var(--leather)",
                  }}
                >
                  {categoryLabel(expert.category)}
                </span>
              </div>
            </header>

            <p
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: 17,
                lineHeight: 1.7,
                color: "var(--charcoal)",
                marginTop: 24,
              }}
            >
              {expert.long_description ?? expert.short_description}
            </p>

            {expert.knowledge_sources && expert.knowledge_sources.length > 0 ? (
              <section style={{ marginTop: 32 }}>
                <h2
                  style={{
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: 13,
                    letterSpacing: 2.5,
                    textTransform: "uppercase",
                    color: "var(--charcoal)",
                  }}
                >
                  Sources de connaissance
                </h2>
                <div style={{ display: "grid", gap: 16 }}>
                  {expert.knowledge_sources.map((layer, i) => (
                    <div
                      key={i}
                      style={{
                        background: "var(--ivory)",
                        border: "0.5px solid var(--stone)",
                        borderRadius: 6,
                        padding: 16,
                      }}
                    >
                      <strong
                        style={{
                          fontFamily: '"Fraunces", serif',
                          color: "var(--charcoal-deep)",
                        }}
                      >
                        {layer.name}
                      </strong>
                      <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                        {layer.items.map((it, j) => (
                          <li
                            key={j}
                            style={{
                              fontFamily: '"DM Sans", sans-serif',
                              fontSize: 13,
                              color: "var(--graphite)",
                            }}
                          >
                            {it.title ?? it.url ?? it.description ?? "—"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <div
              style={{
                marginTop: 32,
                display: "flex",
                gap: 16,
                fontFamily: '"DM Mono", monospace',
                fontSize: 11,
                letterSpacing: 1.5,
                color: "var(--smoke)",
                textTransform: "uppercase",
              }}
            >
              <span>{expert.years_expertise ?? "—"} ans</span>
              <span>·</span>
              <span>{expert.documents_count ?? 0} documents</span>
              {expert.llm ? (
                <>
                  <span>·</span>
                  <span>{expert.llm}</span>
                </>
              ) : null}
            </div>

            <div style={{ marginTop: 32 }}>
              <button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/foyer",
                    search: { add_expert: expert.slug } as never,
                  })
                }
                style={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: 12,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  padding: "12px 22px",
                  background: "var(--leather)",
                  color: "var(--ivory)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Ajouter à mon Conseil
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
